<script setup>
// §8.6 RosterGrid (P1) — Rostering board. Staff (rows) × days (columns), with
// shift blocks. Shifts are neutral brand-tinted blocks with a LABEL (start–end,
// role) — colour is not identity (D9). "Not started" is a legitimate claim for a
// shift object (§3.5.2), unlike a timesheet day. Empty cells invite assignment.
defineProps({
	days: { type: Array, required: true }, // [{ label, sub }]
	// people: [{ name, shifts: { [dayIndex]: { time, role } | null } }]
	people: { type: Array, required: true },
})
</script>

<template>
	<div class="l-roster-wrap">
		<div class="l-roster" :style="{ '--cols': days.length }">
			<div class="corner"></div>
			<div v-for="(d, i) in days" :key="i" class="colhead">
				<div class="day">{{ d.label }}</div>
				<div class="sub num">{{ d.sub }}</div>
			</div>

			<template v-for="(p, r) in people" :key="r">
				<div class="rowhead">{{ p.name }}</div>
				<div v-for="(d, c) in days" :key="c" class="cell">
					<div v-if="p.shifts[c]" class="shift">
						<span class="time num">{{ p.shifts[c].time }}</span>
						<span class="role">{{ p.shifts[c].role }}</span>
					</div>
					<div v-else class="empty" aria-label="No shift">+</div>
				</div>
			</template>
		</div>
	</div>
</template>

<style scoped>
.l-roster-wrap
{
	overflow-x: auto;
}

.l-roster
{
	display: grid;
	grid-template-columns: 150px repeat(var(--cols), minmax(110px, 1fr));
	border: 1px solid var(--line);
	border-radius: var(--r);
	overflow: hidden;
	min-width: 640px;
}

.corner
{
	background: var(--surface-sunken);
	border-bottom: 1px solid var(--line-strong);
}

.colhead
{
	background: var(--surface-sunken);
	padding: 9px 12px;
	border-bottom: 1px solid var(--line-strong);
	border-left: 1px solid var(--line-soft);
}

.day
{
	font-size: var(--text-chip);
	font-weight: 600;
	color: var(--text-2);
}

.sub
{
	font-size: var(--text-micro);
	color: var(--text-3);
}

.rowhead
{
	display: flex;
	align-items: center;
	padding: 10px 12px;
	font-size: var(--text-sm);
	font-weight: 500;
	color: var(--text);
	border-bottom: 1px solid var(--line-soft);
	background: var(--surface);
}

.cell
{
	padding: 6px;
	border-bottom: 1px solid var(--line-soft);
	border-left: 1px solid var(--line-soft);
	background: var(--surface);
	min-height: 52px;
}

.shift
{
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 6px 8px;
	border-radius: var(--r-sm);
	background: var(--accent-fill);
	border-left: 3px solid var(--accent);
}

.time
{
	font-size: var(--text-chip);
	font-weight: 600;
	color: var(--text);
}

.role
{
	font-size: var(--text-micro);
	color: var(--text-3);
}

.empty
{
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	min-height: 40px;
	color: var(--decorative);
	font-size: 16px;
	border-radius: var(--r-sm);
	cursor: pointer;
}

.empty:hover
{
	background: var(--surface-sunken);
	color: var(--interactive-text);
}
</style>
